import {
  DarkFooterLogo,
  FooterLogo,
  footerLogoSize,
} from '@asset/themes/config';
import { Link } from 'react-router-dom';
import { ThemeType } from '@share/enums';
import { useDebouncedCallback } from 'use-debounce';
import { useEffect, useState } from 'react';
import { useSystemConfig } from 'src/hooks';
import { useTranslation } from 'react-i18next';
import { user } from '@state/user';
import LanguageSwitcher from './LanguageSwitcher';
import packageJson from '@packageJson';

function Footer(): JSX.Element {
  const { t } = useTranslation();
  const { config } = useSystemConfig();
  const { isLogin, theme } = user.useContainer();

  const resourcesList = [
    {
      title: `${t('footer.collection')}`,
      link: '/collection',
      dataTest: 'footer-collection',
    },
    {
      title: `${t('footer.lanunchpad')}`,
      link: '/launchpad',
      dataTest: 'footer-launchpad',
    },
  ];
  const companyList = [
    {
      title: `${t('footer.terms')}`,
      link: `${process.env.REACT_APP_API_BASE_URL}/doc/terms-and-conditions`,
      dataTest: 'footer-terms',
    },
    {
      title: `${t('footer.faq')}`,
      link: '/faq',
      dataTest: 'footer-faq',
    },
  ];

  const [isVisible, setIsVisible] = useState(false);

  const scrollToTop = useDebouncedCallback(
    () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    },
    500,
    { leading: true, trailing: false }
  );

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 500) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility, { passive: true });

    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  return (
    <>
      <footer className="footer-light-style clearfix bg-style pb-4" id="footer">
        <div className="themesflat-container">
          <div className="row pb-5 d-flex ml-3">
            <div className="col-lg-3 col-md-12 col-12">
              <div className="widget widget-logo">
                <div className="logo-footer" id="logo-footer">
                  <Link to="/">
                    {theme === ThemeType.DARK && (
                      <img
                        alt="Footer Logo"
                        className="logo-dark rounded-0"
                        loading="lazy"
                        src={FooterLogo}
                        width={footerLogoSize}
                      />
                    )}
                    {theme === ThemeType.LIGHT && (
                      <img
                        alt="Footer Logo"
                        className="logo-light rounded-0"
                        loading="lazy"
                        src={DarkFooterLogo}
                        width={footerLogoSize}
                      />
                    )}
                  </Link>
                </div>
              </div>
              <LanguageSwitcher />
            </div>
            <div className="col-lg-2 col-md-4 col-sm-5 col-5">
              <div className="widget widget-menu style-1 pt-0">
                <h5 className="title-widget">{t('footer.myAccount')}</h5>
                <ul>
                  <li data-test="footer-myProfile">
                    <Link to={isLogin ? '/profile/my' : '/login'}>
                      {t('footer.profile')}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
            <div className="col-lg-2 col-md-4 col-sm-7 col-7">
              <div className="widget widget-menu style-2 pt-0">
                <h5 className="title-widget">{t('footer.resources')}</h5>
                <ul>
                  {resourcesList.map((item, index) => (
                    <li key={index} data-test={item.dataTest}>
                      {item.link.startsWith('https://') ? (
                        <a href={item.link} rel="noreferrer" target="_blank">
                          {item.title}
                        </a>
                      ) : (
                        <Link to={item.link}>{item.title}</Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-lg-2 col-md-4 col-sm-5 col-5">
              <div className="widget widget-menu fl-st-3 pt-0">
                <h5 className="title-widget">{t('footer.company')}</h5>
                <ul>
                  {companyList.map((item, index) => (
                    <li key={index} data-test={item.dataTest}>
                      {item.link.startsWith('https://') ? (
                        <a href={item.link} rel="noreferrer" target="_blank">
                          {item.title}
                        </a>
                      ) : (
                        <Link to={item.link}>{item.title}</Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 col-sm-7 col-12">
              <div className="widget-social style-1 mg-t32 mt-0 justify-content-center">
                <ul>
                  {config?.social_media.map(
                    (item, index) =>
                      item.url && (
                        <li key={index}>
                          <a
                            data-test={`footer-social-${item.icon?.replace(
                              ' ',
                              '-'
                            )}`}
                            href={item.url}
                            rel="noreferrer"
                            target="_blank"
                          >
                            <i className={item.icon} />
                          </a>
                        </li>
                      )
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="mg-t-20 d-flex ml-5 mt-5 fa-1x flex-wrap text-white">
          {config?.marketplace_copyright}
          {` v${packageJson.version}`}
        </div>
      </footer>
      {isVisible && <Link id="scroll-top" to="#" onClick={scrollToTop} />}
    </>
  );
}

export default Footer;
